import { supabase } from './supabase';
import { getApiUrl } from './sync';
import { isDevFakeAuth } from './devAuth';

function getAccessApiUrl(): string {
  // Owner controls must not inherit a stale per-browser sync API override in
  // production. The compiled production URL is authoritative; local dev still
  // falls back to the normal same-host/override resolver.
  const productionUrl = import.meta.env.VITE_API_URL?.trim();
  return (productionUrl || getApiUrl()).replace(/\/+$/, '');
}

export interface AccessControlUser {
  userId: string;
  email: string | null;
  isOwner: boolean;
  isProtected: boolean;
  accessDisabled: boolean;
  accessOverride: boolean;
  bannedUntil: string | null;
  effectiveAccess: boolean;
  accessReason: string;
  createdAt: string | null;
  lastSignInAt: string | null;
}

export interface AccessControlData {
  privateMode: boolean;
  signupDisabled: boolean | null;
  ownerEmails: string[];
  protectedEmails: string[];
  summary: { total: number; enabled: number; disabled: number; protected: number };
  users: AccessControlUser[];
  asOf: string;
}

async function ownerRequest(path: string, init?: RequestInit): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || (isDevFakeAuth() ? 'dev-fake-owner-token' : null);
  if (!token) throw new Error('No owner auth session');
  return fetch(`${getAccessApiUrl()}/api/dev/access${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });
}

async function parseResponse<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) throw new Error(body.error || `Access request failed: ${response.status}`);
  return body;
}

export async function loadAccessControls(): Promise<AccessControlData> {
  // Hono's mounted `/` handler matches /api/dev/access but not a trailing `/`.
  return parseResponse<AccessControlData>(await ownerRequest(''));
}

export async function updatePrivateMode(enabled: boolean, confirmation = ''): Promise<void> {
  await parseResponse(await ownerRequest('/private-mode', {
    method: 'POST',
    body: JSON.stringify({ enabled, confirmation }),
  }));
}

export async function updateUserAccess(
  userId: string,
  enabled: boolean,
  confirmation = ''
): Promise<void> {
  await parseResponse(await ownerRequest(`/users/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify({ enabled, confirmation }),
  }));
}

export async function bulkDisableUsers(confirmation: string): Promise<number> {
  const result = await parseResponse<{ disabledCount: number }>(await ownerRequest('/bulk-disable', {
    method: 'POST',
    body: JSON.stringify({ confirmation }),
  }));
  return result.disabledCount;
}
